import { Link, NavLink } from "react-router-dom";
import { LayoutDashboard, LogOut, Settings, Shield, UploadCloud, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/hooks/useSettings";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export function Logo({ className = "" }) {
  const { settings } = useSettings();
  return (
    <Link to="/" className={`flex items-center gap-2 ${className}`} data-testid="header-logo">
      {settings.logo_url ? (
        <img src={settings.logo_url} alt={settings.site_name} className="h-8 w-auto" />
      ) : (
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-indigo-600 text-white"><UploadCloud className="h-4 w-4" /></span>
      )}
      <span className="font-display text-lg font-bold tracking-tight text-slate-900">{settings.site_name}</span>
    </Link>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const isAdmin = user && ["admin", "super_admin"].includes(user.role);
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/80 backdrop-blur-md" data-testid="site-header">
      <div className="container-x flex h-14 items-center justify-between sm:h-16">
        <Logo />
        <nav className="flex items-center gap-1 sm:gap-2" aria-label="Main">
          <NavLink to="/" className={({ isActive }) => `hidden rounded-lg px-3 py-2 text-sm font-medium sm:block ${isActive ? "text-indigo-600" : "text-slate-600 hover:text-slate-900"}`} data-testid="nav-upload">Upload</NavLink>
          <NavLink to="/blog" className={({ isActive }) => `hidden rounded-lg px-3 py-2 text-sm font-medium sm:block ${isActive ? "text-indigo-600" : "text-slate-600 hover:text-slate-900"}`} data-testid="nav-blog">Blog</NavLink>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-1 flex items-center gap-2 rounded-full border border-slate-200 p-1 pr-2 hover:bg-slate-50" data-testid="user-menu-trigger" aria-label="Account menu">
                  <Avatar className="h-7 w-7"><AvatarImage src={user.picture || undefined} alt="" /><AvatarFallback className="bg-indigo-100 text-xs font-semibold text-indigo-700">{(user.name || user.email || "?")[0].toUpperCase()}</AvatarFallback></Avatar>
                  <Menu className="h-4 w-4 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-xs text-slate-500 truncate" data-testid="user-menu-email">{user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/dashboard" data-testid="menu-dashboard"><LayoutDashboard className="mr-2 h-4 w-4" />Dashboard</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/settings" data-testid="menu-settings"><Settings className="mr-2 h-4 w-4" />Account</Link></DropdownMenuItem>
                {isAdmin && <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin"><Shield className="mr-2 h-4 w-4" />Admin panel</Link></DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="text-slate-700"><Link to="/login" data-testid="nav-login">Log in</Link></Button>
              {settings.registration_enabled && <Button asChild size="sm" className="rounded-full bg-indigo-600 hover:bg-indigo-700"><Link to="/register" data-testid="nav-register">Register</Link></Button>}
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
